import { useState, useEffect } from 'react';
import { CheckCircle, Play, Square, Truck } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from "@/components/ui/alert";
import { OtherSiteVehicleConfirm, isOtherSiteError, errorDetailText } from '@/components/OtherSiteVehicleConfirm';
import ServiceInspection from '@/components/ServiceInspection';

const isInspectionRequired = error => error?.response?.status === 409 && error.response.data?.detail?.code === 'INSPECTION_REQUIRED';

const PhotoUpload = ({ user }) => {
  const [otherSitePending, setOtherSitePending] = useState(null);
  const [sessionStatus, setSessionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vehiclePlate, setVehiclePlate] = useState('');
  const [pdaId, setPdaId] = useState('');
  const [pdas, setPdas] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [tourType, setTourType] = useState('');
  const [assistanceComment, setAssistanceComment] = useState('');
  const [backupStats, setBackupStats] = useState({ pointsDelivered: '', returns: '' });
  const [employeeSiteType, setEmployeeSiteType] = useState(null);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [kmDebut, setKmDebut] = useState('');
  const [kmFin, setKmFin] = useState('');
  const [deliveryStats, setDeliveryStats] = useState({
    delivered: '',
    returned: '',
    picked: '',
    nonCollected: ''
  });
  const [activeAssignment, setActiveAssignment] = useState(null);
  const [showVehicleChange, setShowVehicleChange] = useState(false);
  const [changeReason, setChangeReason] = useState('');
  const [newVehiclePlate, setNewVehiclePlate] = useState('');
  const [changingVehicle, setChangingVehicle] = useState(false);

  // Amazon tour types
  const AMAZON_TOUR_TYPES = [
    { value: 'SDA+C', label: 'SDA+C' },
    { value: 'SDA', label: 'SDA' },
    { value: 'SDB', label: 'SDB' },
    { value: 'SDC', label: 'SDC' },
    { value: 'SDA+B', label: 'SDA+B' },
    { value: 'SDB+C', label: 'SDB+C' },
    { value: 'NEXT', label: 'NEXT' },
    { value: "Assistance chef d'équipe", label: "Assistance chef d'équipe" },
    { value: 'Backup', label: 'Backup' },
  ];

  useEffect(() => {
    if (user?.user_id) {
      fetchData();
      fetchPdas();
    }
  }, [user?.user_id]);

  const fetchPdas = async () => {
    try {
      const res = await axios.get(`${API}/employee/pdas`, { withCredentials: true });
      setPdas(res.data.pdas || []);
    } catch (error) {
      console.error('Failed to fetch PDAs:', error);
    }
  };

  const fetchData = async () => {
    try {
      const [sessionRes, userRes] = await Promise.all([
        axios.get(`${API}/photos/session-status`, { withCredentials: true }),
        axios.get(`${API}/auth/me`, { withCredentials: true })
      ]);
      setSessionStatus(sessionRes.data);

      let currentAssignment = null;
      try {
        const assignmentRes = await axios.get(`${API}/service/vehicle-assignment/active`, { withCredentials: true });
        currentAssignment = assignmentRes.data?.active_assignment || null;
        setActiveAssignment(currentAssignment);
      } catch (assignmentError) {
        console.error('Failed to fetch active vehicle assignment:', assignmentError);
      }

      // Pre-fill vehicle plate from active assignment (source of truth), fallback start session
      const startData = sessionRes.data?.start;
      if (startData?.complete && !sessionRes.data?.end?.complete) {
        if (currentAssignment?.plate_number) {
          setVehiclePlate(currentAssignment.plate_number);
        } else if (startData.vehicle_plate) {
          setVehiclePlate(startData.vehicle_plate);
        }
      }
      
      const userData = userRes.data;
      let detectedSiteType = null;
      
      if (userData?.site_type) {
        detectedSiteType = userData.site_type;
      } else if (userData?.site_name) {
        const name = userData.site_name.toLowerCase();
        if (name.includes('colis') || name.includes('privé') || name.includes('prive') || name === 'cp') {
          detectedSiteType = 'colis_prive';
        } else {
          detectedSiteType = 'amazon';
        }
      } else if (userData?.site_id) {
        detectedSiteType = 'amazon';
      }
      
      if (detectedSiteType) {
        setEmployeeSiteType(detectedSiteType);
      }

      // Prefill mission data from planning (today business date from backend)
      if (user?.user_id) {
        const schedRes = await axios.get(`${API}/schedule/today`, { withCredentials: true });
        const todaySchedule = schedRes.data;
        if (todaySchedule) {
          if (!tourType && todaySchedule.tour_type) {
            setTourType(todaySchedule.tour_type);
          }
          if (!assistanceComment && todaySchedule.assistance_comment) {
            setAssistanceComment(todaySchedule.assistance_comment);
          }
          if ((backupStats.pointsDelivered === '' || backupStats.returns === '') && (todaySchedule.backup_points_delivered !== null || todaySchedule.backup_returns !== null)) {
            setBackupStats({
              pointsDelivered: todaySchedule.backup_points_delivered ?? '',
              returns: todaySchedule.backup_returns ?? ''
            });
          }
          if (!startTime && todaySchedule.start_time) {
            setStartTime(todaySchedule.start_time);
          }
          if (!endTime && todaySchedule.end_time) {
            setEndTime(todaySchedule.end_time);
          }
          if (!vehiclePlate && todaySchedule.vehicle_plate) {
            setVehiclePlate(todaySchedule.vehicle_plate);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkPlateAvailability = async (plate, setter) => {
    try {
      const res = await axios.get(`${API}/fleet/daily/vehicle-availability`, {
        params: { plate: plate.trim() }, withCredentials: true
      });
      setter(res.data);
    } catch { setter(null); }
  };

  const [plateCheck, setPlateCheck] = useState(null);
  const [newPlateCheck, setNewPlateCheck] = useState(null);
  const [inspection, setInspection] = useState(null); // { plate, action: 'start' | 'change' }
  const refreshPlateChecks = () => { if (vehiclePlate.trim()) checkPlateAvailability(vehiclePlate, setPlateCheck); if (newVehiclePlate.trim()) checkPlateAvailability(newVehiclePlate, setNewPlateCheck); };

  useEffect(() => {
    if (!vehiclePlate || vehiclePlate.trim().length < 5 || sessionStatus?.start?.complete) { setPlateCheck(null); return; }
    const t = setTimeout(() => checkPlateAvailability(vehiclePlate, setPlateCheck), 600);
    return () => clearTimeout(t);
  }, [vehiclePlate, sessionStatus]);

  useEffect(() => {
    if (!newVehiclePlate || newVehiclePlate.trim().length < 5) { setNewPlateCheck(null); return; }
    const t = setTimeout(() => checkPlateAvailability(newVehiclePlate, setNewPlateCheck), 600);
    return () => clearTimeout(t);
  }, [newVehiclePlate]);

  const [serviceVehicles, setServiceVehicles] = useState(null);

  useEffect(() => {
    axios.get(`${API}/service/vehicles`, { withCredentials: true })
      .then(res => setServiceVehicles(res.data))
      .catch(() => setServiceVehicles(null));
  }, []);

  const renderVehicleOptions = () => (serviceVehicles?.vehicles || []).map(v => (
    <option key={v.vehicle_id} value={v.plate_number} disabled={!v.available} data-other-site={v.other_site ? 'true' : undefined}>
      {v.plate_number} · {v.brand} {v.model}{v.other_site ? ` · autre site : ${v.site_name}` : ''} · {v.available ? (v.availability_label || 'Disponible') : v.unavailable_reason}{v.available && v.availability !== 'a_corriger' ? (v.inspection_required ? ' · Inspection à faire' : ' · Inspection OK') : ''}
    </option>
  ));

  const handleStartSession = async (confirmOtherSite = false) => {
    if (!vehiclePlate.trim()) {
      setSubmitError('Veuillez saisir la plaque du véhicule');
      return;
    }
    if (!startTime) {
      setSubmitError('Veuillez saisir l\'heure de début');
      return;
    }
    if (!kmDebut || parseInt(kmDebut) < 0) {
      setSubmitError('Veuillez saisir un kilométrage de début valide');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);

    try {
      await axios.post(`${API}/service/start`, {
        vehicle_plate: vehiclePlate.toUpperCase().trim(),
        start_time: startTime,
        km_debut: parseInt(kmDebut),
        whatsapp_photos_posted: true,
        confirm_other_site: confirmOtherSite
      }, { withCredentials: true });

      setOtherSitePending(null);
      setSubmitSuccess('Début de service enregistré !');
      setVehiclePlate('');
      setStartTime('');
      setKmDebut('');
      fetchData();
    } catch (error) {
      if (isInspectionRequired(error)) {
        setInspection({ plate: vehiclePlate.toUpperCase().trim(), action: 'start' });
        return;
      }
      if (isOtherSiteError(error)) {
        setOtherSitePending({ action: 'start', plate: vehiclePlate.toUpperCase().trim(), detail: error.response.data.detail });
        return;
      }
      setOtherSitePending(null);
      setSubmitError(errorDetailText(error, 'Erreur lors de l\'enregistrement'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEndSession = async () => {
    if (!vehiclePlate.trim()) {
      setSubmitError('Veuillez saisir la plaque du véhicule');
      return;
    }

    if (!endTime) {
      setSubmitError('Veuillez saisir l\'heure de fin');
      return;
    }

    if (!kmFin || parseInt(kmFin) < 0) {
      setSubmitError('Veuillez saisir un kilométrage de fin valide');
      return;
    }

    // Validate km_fin > km_debut if km_debut was recorded
    const startKm = sessionStatus?.start?.km_debut;
    if (startKm !== null && startKm !== undefined && parseInt(kmFin) <= startKm) {
      setSubmitError(`Le km de fin (${kmFin}) doit être supérieur au km de début (${startKm})`);
      return;
    }

    if (employeeSiteType === 'amazon' && !tourType) {
      setSubmitError('Veuillez sélectionner le type de tournée');
      return;
    }

    if (employeeSiteType === 'amazon' && tourType === "Assistance chef d'équipe" && !assistanceComment.trim()) {
      setSubmitError("Commentaire obligatoire pour la prestation Assistance chef d'équipe");
      return;
    }

    if (employeeSiteType === 'amazon' && tourType === 'Backup') {
      if (backupStats.pointsDelivered === '' || parseInt(backupStats.pointsDelivered, 10) < 0) {
        setSubmitError('Veuillez saisir le nombre de points livrés pour la prestation Backup');
        return;
      }
      if (backupStats.returns === '' || parseInt(backupStats.returns, 10) < 0) {
        setSubmitError('Veuillez saisir le nombre de retours pour la prestation Backup');
        return;
      }
    }

    if (employeeSiteType === 'colis_prive') {
      if (!deliveryStats.delivered && deliveryStats.delivered !== '0') {
        setSubmitError('Veuillez saisir le nombre de colis livrés');
        return;
      }
      if (!deliveryStats.returned && deliveryStats.returned !== '0') {
        setSubmitError('Veuillez saisir le nombre de colis retournés');
        return;
      }
      if (!deliveryStats.nonCollected && deliveryStats.nonCollected !== '0') {
        setSubmitError('Veuillez saisir le nombre de colis non collectés');
        return;
      }
      if (!pdaId) {
        setSubmitError('Veuillez sélectionner le PDA utilisé');
        return;
      }
    }

    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);

    try {
      const payload = {
        vehicle_plate: vehiclePlate.toUpperCase().trim(),
        tour_type: employeeSiteType === 'amazon' ? tourType : null,
        assistance_comment: employeeSiteType === 'amazon' && tourType === "Assistance chef d'équipe" ? assistanceComment.trim() : null,
        backup_points_delivered: employeeSiteType === 'amazon' && tourType === 'Backup' ? parseInt(backupStats.pointsDelivered) || 0 : null,
        backup_returns: employeeSiteType === 'amazon' && tourType === 'Backup' ? parseInt(backupStats.returns) || 0 : null,
        delivered: employeeSiteType === 'colis_prive' ? parseInt(deliveryStats.delivered) || 0 : null,
        returned: employeeSiteType === 'colis_prive' ? parseInt(deliveryStats.returned) || 0 : null,
        picked: employeeSiteType === 'colis_prive' ? parseInt(deliveryStats.picked) || 0 : null,
        non_collected: employeeSiteType === 'colis_prive' ? parseInt(deliveryStats.nonCollected) || 0 : null,
        pda_id: employeeSiteType === 'colis_prive' ? pdaId : null,
        end_time: endTime,
        km_fin: parseInt(kmFin),
      };

      await axios.post(`${API}/service/end`, payload, { withCredentials: true });

      setSubmitSuccess('Fin de service enregistrée ! En attente de validation.');
      setVehiclePlate('');
      setTourType('');
      setPdaId('');
      setEndTime('');
      setKmFin('');
      setAssistanceComment('');
      setBackupStats({ pointsDelivered: '', returns: '' });
      setDeliveryStats({ delivered: '', returned: '', picked: '', nonCollected: '' });
      fetchData();
    } catch (error) {
      setSubmitError(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVehicleChange = async (confirmOtherSite = false) => {
    if (!changeReason) {
      setSubmitError('Veuillez sélectionner le motif du changement de véhicule');
      return;
    }
    if (!newVehiclePlate.trim()) {
      setSubmitError('Veuillez saisir la plaque du nouveau véhicule');
      return;
    }

    setChangingVehicle(true);
    setSubmitError(null);
    setSubmitSuccess(null);

    try {
      const response = await axios.post(`${API}/service/vehicle-change`, {
        new_vehicle_plate: newVehiclePlate.toUpperCase().trim(),
        reason: changeReason,
        confirm_other_site: confirmOtherSite
      }, { withCredentials: true });

      setOtherSitePending(null);
      const confirmedPlate = response.data?.new_vehicle_plate || newVehiclePlate.toUpperCase().trim();
      setSubmitSuccess(`Changement de véhicule enregistré. Nouveau véhicule : ${confirmedPlate}.`);
      setVehiclePlate(confirmedPlate);
      setShowVehicleChange(false);
      setChangeReason('');
      setNewVehiclePlate('');
      fetchData();
    } catch (error) {
      if (isInspectionRequired(error)) {
        setInspection({ plate: newVehiclePlate.toUpperCase().trim(), action: 'change' });
        return;
      }
      if (isOtherSiteError(error)) {
        setOtherSitePending({ action: 'change', plate: newVehiclePlate.toUpperCase().trim(), detail: error.response.data.detail });
        return;
      }
      setOtherSitePending(null);
      setSubmitError(errorDetailText(error, 'Erreur lors du changement de véhicule'));
    } finally {
      setChangingVehicle(false);
    }
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  const startComplete = sessionStatus?.start?.complete;
  const endComplete = sessionStatus?.end?.complete;

  return (
    <Layout user={user}>
      <div className="max-w-3xl mx-auto space-y-8 px-1" data-testid="validation-mission-page">
        <div className="text-center space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Validation Mission</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">Parcours de validation</h1>
          <p className="text-sm text-slate-500">Simple, rapide et lisible — démarrez, terminez, validez.</p>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Card className={`rounded-2xl ${startComplete ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white'}`}>
            <CardContent className="p-4 text-center">
              <Play className={`w-8 h-8 mx-auto mb-2 ${startComplete ? 'text-green-600' : 'text-gray-400'}`} />
              <p className="font-medium">Début</p>
              <p className="text-sm text-gray-500">{startComplete ? '✅ Validé' : '⏳ En attente'}</p>
            </CardContent>
          </Card>
          <Card className={`rounded-2xl ${endComplete ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white'}`}>
            <CardContent className="p-4 text-center">
              <Square className={`w-8 h-8 mx-auto mb-2 ${endComplete ? 'text-green-600' : 'text-gray-400'}`} />
              <p className="font-medium">Fin</p>
              <p className="text-sm text-gray-500">{endComplete ? '✅ Validé' : '⏳ En attente'}</p>
            </CardContent>
          </Card>
        </div>

        {/* Alerts */}
        {submitError && (
          <Alert variant="destructive">
            <AlertDescription>{submitError}</AlertDescription>
          </Alert>
        )}
        {submitSuccess && (
          <Alert className="border-green-500 bg-green-50">
            <AlertDescription className="text-green-700">{submitSuccess}</AlertDescription>
          </Alert>
        )}

        {/* Start Service Form */}
        {!startComplete && (
          <Card className="rounded-2xl border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Play className="w-5 h-5 text-blue-600" />
                Début de Service
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="plate-start">Plaque du véhicule (votre site en premier, puis les autres sites)</Label>
                <select
                  id="plate-start"
                  value={vehiclePlate}
                  onChange={(e) => setVehiclePlate(e.target.value)}
                  className="mt-1 w-full px-3 py-2 border rounded-md bg-white text-sm"
                  data-testid="plate-start-input"
                >
                  <option value="">— Sélectionnez un véhicule —</option>
                  {renderVehicleOptions()}
                </select>
                {serviceVehicles && serviceVehicles.vehicles?.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1" data-testid="no-site-vehicles-warning">
                    {serviceVehicles.message || 'Aucun véhicule disponible sur votre site.'}
                  </p>
                )}
                {plateCheck?.known && !plateCheck.available && (
                  <p className="text-xs font-medium text-red-600 mt-1" data-testid="plate-start-availability-warning">⛔ {plateCheck.label}</p>
                )}
                {plateCheck?.known && plateCheck.available && !plateCheck.other_site && !plateCheck.inspection_required && (
                  <p className="text-xs text-green-600 mt-1" data-testid="plate-start-availability-ok">✅ Disponible · inspection de prise de service complète (le départ reste soumis aux contrôles du serveur)</p>
                )}
                {plateCheck?.known && plateCheck.available && plateCheck.to_fix && (
                  <p className="text-xs text-orange-800 mt-1 font-medium" data-testid="plate-start-to-fix">🛠️ {plateCheck.label}</p>
                )}
                {plateCheck?.known && plateCheck.available && plateCheck.inspection_required && (
                  <div className="mt-2 rounded-lg border border-indigo-200 bg-indigo-50 p-3 space-y-2" data-testid="plate-start-inspection-required">
                    <p className="text-sm text-indigo-900 font-medium">🔎 {plateCheck.to_fix ? 'Après correction, un re-contrôle est obligatoire avant le départ.' : 'Inspection du véhicule obligatoire avant cette prise de service (à faire autour du véhicule ; la durée dépend de la vérification physique).'}</p>
                    <Button type="button" variant="outline" className="w-full min-h-[46px] rounded-xl border-indigo-400 text-indigo-900" onClick={() => { setSubmitError(null); setInspection({ plate: vehiclePlate.toUpperCase().trim(), action: 'start' }); }} data-testid="start-inspection-btn">Faire l’inspection maintenant</Button>
                  </div>
                )}
                {plateCheck?.known && plateCheck.available && plateCheck.other_site && (
                  <p className="text-xs font-medium text-amber-700 mt-1" data-testid="plate-start-other-site-warning">⚠️ {plateCheck.label}</p>
                )}
              </div>
              <div>
                <Label htmlFor="start-time">Heure de début</Label>
                <Input
                  id="start-time"
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="mt-1"
                  data-testid="start-time-input"
                />
              </div>
              <div>
                <Label htmlFor="km-debut">Kilométrage de début *</Label>
                <Input
                  id="km-debut"
                  type="number"
                  min="0"
                  placeholder="Ex: 45230"
                  value={kmDebut}
                  onChange={(e) => setKmDebut(e.target.value)}
                  className="mt-1"
                  data-testid="km-debut-input"
                />
              </div>
              <Button 
                onClick={() => handleStartSession(false)} 
                disabled={submitting || !vehiclePlate.trim() || !startTime || !kmDebut}
                className="w-full rounded-xl min-h-[46px] bg-indigo-600 hover:bg-indigo-700"
                data-testid="start-service-btn"
              >
                {submitting ? 'Envoi en cours...' : 'Valider Début de Service'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* End Service Form */}
        {startComplete && !endComplete && (
          <Card className="rounded-2xl border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Square className="w-5 h-5 text-orange-600" />
                Fin de Service
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Recap of start session */}
              {sessionStatus?.start?.start_time && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                  <p className="font-medium text-blue-800">Votre service a commencé à {sessionStatus.start.start_time}</p>
                  {sessionStatus.start.vehicle_plate && (
                    <p className="text-blue-600">Véhicule : {sessionStatus.start.vehicle_plate}</p>
                  )}
                  {sessionStatus.start.km_debut != null && (
                    <p className="text-blue-600">Km début : {sessionStatus.start.km_debut.toLocaleString()} km</p>
                  )}
                </div>
              )}

              {activeAssignment?.plate_number && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2" data-testid="active-vehicle-box">
                  <p className="text-sm text-slate-800" data-testid="active-vehicle-plate-text">
                    <span className="font-medium">Véhicule en cours :</span> <span className="font-mono font-bold">{activeAssignment.plate_number}</span>
                    {activeAssignment.started_at && (
                      <span className="text-slate-500"> — depuis {new Date(activeAssignment.started_at).toLocaleTimeString('fr-FR', { timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit' })}</span>
                    )}
                  </p>
                  {!showVehicleChange ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => { setShowVehicleChange(true); setSubmitError(null); }}
                      className="rounded-lg"
                      data-testid="vehicle-change-toggle-btn"
                    >
                      Changer de véhicule (panne ou remplacement)
                    </Button>
                  ) : (
                    <div className="space-y-3 border-t border-slate-200 pt-3" data-testid="vehicle-change-form">
                      <div>
                        <Label>Motif du changement *</Label>
                        <div className="grid grid-cols-2 gap-2 mt-1">
                          <Button
                            type="button"
                            variant={changeReason === 'panne' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setChangeReason('panne')}
                            className="rounded-lg min-h-[40px]"
                            data-testid="vehicle-change-reason-panne"
                          >
                            Panne
                          </Button>
                          <Button
                            type="button"
                            variant={changeReason === 'changement_de_vehicule' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setChangeReason('changement_de_vehicule')}
                            className="rounded-lg min-h-[40px]"
                            data-testid="vehicle-change-reason-remplacement"
                          >
                            Changement de véhicule
                          </Button>
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="new-plate">Nouvelle plaque * (votre site en premier, puis les autres sites)</Label>
                        <select
                          id="new-plate"
                          value={newVehiclePlate}
                          onChange={(e) => setNewVehiclePlate(e.target.value)}
                          className="mt-1 w-full px-3 py-2 border rounded-md bg-white text-sm"
                          data-testid="vehicle-change-new-plate-input"
                        >
                          <option value="">— Sélectionnez un véhicule —</option>
                          {renderVehicleOptions()}
                        </select>
                        {newPlateCheck?.known && !newPlateCheck.available && (
                          <p className="text-xs font-medium text-red-600 mt-1" data-testid="vehicle-change-availability-warning">⛔ {newPlateCheck.label}</p>
                        )}
                        {newPlateCheck?.known && newPlateCheck.available && !newPlateCheck.other_site && (
                          <p className="text-xs text-green-600 mt-1" data-testid="vehicle-change-availability-ok">{newPlateCheck.inspection_required ? '🔎 Véhicule disponible · inspection obligatoire avant utilisation (proposée à la validation)' : '✅ Véhicule disponible · inspection réalisée'}</p>
                        )}
                        {newPlateCheck?.known && newPlateCheck.available && newPlateCheck.other_site && (
                          <p className="text-xs font-medium text-amber-700 mt-1" data-testid="vehicle-change-other-site-warning">⚠️ {newPlateCheck.label}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          onClick={() => handleVehicleChange(false)}
                          disabled={changingVehicle || !changeReason || !newVehiclePlate.trim()}
                          size="sm"
                          className="rounded-lg bg-indigo-600 hover:bg-indigo-700"
                          data-testid="vehicle-change-submit-btn"
                        >
                          {changingVehicle ? 'Enregistrement...' : 'Confirmer le changement'}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => { setShowVehicleChange(false); setChangeReason(''); setNewVehiclePlate(''); }}
                          className="rounded-lg"
                          data-testid="vehicle-change-cancel-btn"
                        >
                          Annuler
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              <div>
                <Label htmlFor="plate-end">Plaque du véhicule</Label>
                <Input
                  id="plate-end"
                  placeholder="Ex: AB-123-CD"
                  value={vehiclePlate}
                  onChange={(e) => setVehiclePlate(e.target.value.toUpperCase())}
                  className="mt-1"
                  data-testid="plate-end-input"
                />
              </div>

              <div>
                <Label htmlFor="end-time" className="text-base font-semibold">Heure de fin *</Label>
                <Input
                  id="end-time"
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="mt-1 text-lg font-mono h-12"
                  data-testid="end-time-input"
                />
              </div>

              <div>
                <Label htmlFor="km-fin" className="text-base font-semibold">Kilométrage de fin *</Label>
                <Input
                  id="km-fin"
                  type="number"
                  min="0"
                  placeholder="Ex: 45380"
                  value={kmFin}
                  onChange={(e) => setKmFin(e.target.value)}
                  className="mt-1 text-lg font-mono h-12"
                  data-testid="km-fin-input"
                />
                {sessionStatus?.start?.km_debut != null && kmFin && parseInt(kmFin) > sessionStatus.start.km_debut && (
                  <p className="text-sm text-green-600 mt-1">
                    Distance : {(parseInt(kmFin) - sessionStatus.start.km_debut).toLocaleString()} km
                  </p>
                )}
                {sessionStatus?.start?.km_debut != null && kmFin && parseInt(kmFin) <= sessionStatus.start.km_debut && (
                  <p className="text-sm text-red-600 mt-1">
                    Le km de fin doit être supérieur à {sessionStatus.start.km_debut.toLocaleString()} km
                  </p>
                )}
              </div>

              {/* Amazon Form */}
              {employeeSiteType === 'amazon' && (
                <div>
                  <Label>Type de tournée (Prestation)</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 mt-2">
                    {AMAZON_TOUR_TYPES.map((type) => (
                      <Button
                        key={type.value}
                        variant={tourType === type.value ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => {
                          setTourType(type.value);
                          if (type.value !== "Assistance chef d'équipe") setAssistanceComment('');
                          if (type.value !== 'Backup') setBackupStats({ pointsDelivered: '', returns: '' });
                        }}
                        className={`text-xs rounded-lg min-h-[40px] ${tourType === type.value ? 'shadow-sm' : ''}`}
                        data-testid={`amazon-prestation-${type.value.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`}
                      >
                        {type.label}
                      </Button>
                    ))}
                  </div>

                  {tourType === "Assistance chef d'équipe" && (
                    <div className="mt-3">
                      <Label htmlFor="assistance-comment">Commentaire détaillé *</Label>
                      <Input
                        id="assistance-comment"
                        value={assistanceComment}
                        onChange={(e) => setAssistanceComment(e.target.value)}
                        placeholder="Détail de la prestation réalisée"
                        className="mt-1"
                        data-testid="assistance-comment-input"
                      />
                    </div>
                  )}

                  {tourType === 'Backup' && (
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="backup-points-delivered">Nombre de points livrés *</Label>
                        <Input
                          id="backup-points-delivered"
                          type="number"
                          min="0"
                          value={backupStats.pointsDelivered}
                          onChange={(e) => setBackupStats(prev => ({ ...prev, pointsDelivered: e.target.value }))}
                          placeholder="Ex: 85"
                          className="mt-1"
                          data-testid="backup-points-delivered-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="backup-returns">Nombre de retours *</Label>
                        <Input
                          id="backup-returns"
                          type="number"
                          min="0"
                          value={backupStats.returns}
                          onChange={(e) => setBackupStats(prev => ({ ...prev, returns: e.target.value }))}
                          placeholder="Ex: 12"
                          className="mt-1"
                          data-testid="backup-returns-input"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Colis Privé Form */}
              {employeeSiteType === 'colis_prive' && (
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="pda">PDA utilisé *</Label>
                    <select
                      id="pda"
                      value={pdaId}
                      onChange={(e) => setPdaId(e.target.value)}
                      className="w-full mt-1 px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="">Sélectionner un PDA</option>
                      {pdas.map(pda => (
                        <option key={pda.pda_id} value={pda.pda_id}>{pda.numero}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="delivered">Colis livrés *</Label>
                    <Input
                      id="delivered"
                      type="number"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      min="0"
                      placeholder="Nombre de colis livrés"
                      value={deliveryStats.delivered}
                      onChange={(e) => setDeliveryStats(prev => ({ ...prev, delivered: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="returned">Colis retournés *</Label>
                    <Input
                      id="returned"
                      type="number"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      min="0"
                      placeholder="Nombre de colis retournés"
                      value={deliveryStats.returned}
                      onChange={(e) => setDeliveryStats(prev => ({ ...prev, returned: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="nonCollected">Non collectés *</Label>
                    <Input
                      id="nonCollected"
                      type="number"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      min="0"
                      placeholder="Nombre de colis non collectés"
                      value={deliveryStats.nonCollected}
                      onChange={(e) => setDeliveryStats(prev => ({ ...prev, nonCollected: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="picked">Collectes</Label>
                    <Input
                      id="picked"
                      type="number"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      min="0"
                      placeholder="Nombre de collectes"
                      value={deliveryStats.picked}
                      onChange={(e) => setDeliveryStats(prev => ({ ...prev, picked: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                </div>
              )}

              <Button 
                onClick={handleEndSession} 
                disabled={submitting || !vehiclePlate.trim() || !endTime || !kmFin}
                className="w-full bg-orange-600 hover:bg-orange-700 rounded-xl min-h-[46px]"
                data-testid="end-service-btn"
              >
                {submitting ? 'Envoi en cours...' : 'Valider Fin de Service'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* All Complete */}
        {startComplete && endComplete && (
          <Card className="border-green-500 bg-green-50">
            <CardContent className="p-6 text-center">
              <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-600" />
              <h2 className="text-xl font-bold text-green-700">Mission Terminée !</h2>
              <p className="text-green-600 mt-2">Votre mission du jour a été validée avec succès.</p>
              <p className="text-sm text-gray-500 mt-4">En attente de validation par l’administration.</p>
            </CardContent>
          </Card>
        )}

        {/* No site assigned */}
        {!employeeSiteType && !loading && (
          <Alert variant="destructive">
            <AlertDescription>
              Aucun site n’est assigné à votre profil. Contactez l’administration.
            </AlertDescription>
          </Alert>
        )}
      </div>
      {inspection && <ServiceInspection plate={inspection.plate} onClose={() => { setInspection(null); refreshPlateChecks(); }} onCleared={() => { const act = inspection.action; setInspection(null); setSubmitError(null); refreshPlateChecks(); setTimeout(() => document.querySelector(act === 'change' ? "[data-testid='vehicle-change-submit-btn']" : (!startTime ? "[data-testid='start-time-input']" : !kmDebut ? "[data-testid='km-debut-input']" : "[data-testid='start-service-btn']"))?.focus(), 150); }} />}
      <OtherSiteVehicleConfirm
        pending={otherSitePending}
        busy={submitting || changingVehicle}
        onCancel={() => setOtherSitePending(null)}
        onConfirm={() => (otherSitePending?.action === 'change' ? handleVehicleChange(true) : handleStartSession(true))}
      />
    </Layout>
  );
};

export default PhotoUpload;
